/**
 * Partify Native Fitment Resolver  — Phase 5
 * --------------------------------------------
 * Determines product↔vehicle fitment using collection membership.
 * Zero API calls — uses Liquid-injected collection handles.
 *
 * Strategy:
 *   1. Read active vehicle from localStorage.searchTerms[0]
 *   2. Read product collections from window.__PARTIFY_PRODUCT_COLLECTIONS
 *   3. Build vehicle collection handle (same logic as generateCollectionHandle)
 *   4. Check membership via progressive match:
 *      full ymm → year-make-model-submodel → year-make-model
 *   5. Feed result into PartifyAdapter.emitFitmentState()
 *
 * Feature flag: window.__PARTIFY_NATIVE_FITMENT
 *   false (default)  — resolver exists but never auto-runs
 *   'shadow'         — runs alongside EasySearch, logs comparison
 *   'primary'        — drives PDP fitment directly via shim
 *
 * Rollback: set window.__PARTIFY_NATIVE_FITMENT = false  (or don't set it)
 */
(function () {
	"use strict";

	/* ───────── helpers ───────── */

	function getActiveVehicle() {
		try {
			const raw = localStorage.getItem("searchTerms");
			const arr = JSON.parse(raw);
			if (Array.isArray(arr) && arr.length && arr[0].model) return arr[0];
		} catch (_) {}
		return null;
	}

	/**
	 * Replicate generateCollectionHandle() from global-library.js L4114.
	 * Input: arbitrary string like "2012 Hyundai Accent GLS 1.6L L4"
	 * Output: "2012-hyundai-accent-gls-16l-l4"
	 */
	function toHandle(str) {
		if (!str) return "";
		return str
			.toLowerCase()
			.replace(/[./+:]/g, "-")
			.replace(/\s+/g, "-")
			.replace(/[^\w-]+/g, "")
			.replace(/--+/g, "-")
			.replace(/-+$/, "");
	}

	function buildHandleCandidates(vehicle) {
		if (!vehicle) return [];
		const parts = [vehicle.year, vehicle.make, vehicle.model];
		const full = parts
			.concat(
				[vehicle.submodel, vehicle.engine].filter(function (v) {
					return v && String(v).trim();
				}),
			)
			.join(" ");
		return [vehicle.fitmentYmm, full]
			.map(toHandle)
			.filter(function (handle, index, list) {
				return handle && list.indexOf(handle) === index;
			});
	}

	function hasExactSpecificFitment(vehicle, collections, lookup) {
		if (
			!vehicle ||
			!Array.isArray(collections) ||
			!lookup ||
			typeof lookup !== "object" ||
			(vehicle.submodel && String(vehicle.submodel).trim())
		) return false;
		const year = String(vehicle.year || "").trim();
		const make = toHandle(vehicle.make);
		const model = toHandle(vehicle.model);
		if (!year || !make || !model) return false;
		for (let i = 0; i < collections.length; i++) {
			const handle = collections[i];
			if (!Object.prototype.hasOwnProperty.call(lookup, handle)) continue;
			const row = lookup[handle];
			if (!row || typeof row !== "object" || !String(row.submodel || "").trim()) continue;
			if (
				String(row.year || "").trim() === year &&
				toHandle(row.make) === make &&
				toHandle(row.model) === model
			)
				return true;
		}
		return false;
	}

	/* ───────── core resolver ───────── */

	function resolveVehicle(vehicle) {
		if (!vehicle) return { state: "no-vehicle", vehicle: null };

		const collections = window.__PARTIFY_PRODUCT_COLLECTIONS;
		if (!Array.isArray(collections) || !collections.length) {
			return { state: "error", reason: "no-collection-data", vehicle: vehicle };
		}

		// Build a Set for O(1) lookup
		const handleSet = {};
		for (let i = 0; i < collections.length; i++) handleSet[collections[i]] = 1;

		// Progressive match: specific → broad
		const candidates = buildHandleCandidates(vehicle);
		for (let i = 0; i < candidates.length; i++) {
			if (handleSet[candidates[i]]) {
				return {
					state: "success",
					vehicle: vehicle,
					matchedHandle: candidates[i],
					matchLevel: i === 0 ? "exact" : "broad",
					candidatesTried: candidates,
					collectionCount: collections.length,
				};
			}
		}

		const result = {
			state: "fail",
			vehicle: vehicle,
			candidatesTried: candidates,
			collectionCount: collections.length,
		};
		if (hasExactSpecificFitment(vehicle, collections, window.fitmentLookup)) {
			result.reason = "missing-specificity";
		}
		return result;
	}

	function resolve() {
		return resolveVehicle(getActiveVehicle());
	}

	/* ───────── build fail-link URL ───────── */

	function buildFailHref(vehicle) {
		if (!vehicle || !vehicle.ymm) return "/collections/all";
		return "/collections/" + toHandle(vehicle.fitmentYmm || vehicle.ymm);
	}

	/* ───────── run logic ───────── */

	function clearStalePreselect() {
		try {
			var raw = localStorage.getItem("easysearch-preselect-fitment");
			if (!raw) return;
			var parsed = JSON.parse(raw);
			if (parsed && parsed.value) {
				localStorage.setItem(
					"easysearch-preselect-fitment",
					JSON.stringify({ value: "", expires: parsed.expires || "" }),
				);
			}
		} catch (_) {}
	}

	function run() {
		var mode = window.__PARTIFY_NATIVE_FITMENT;
		if (!mode) return;

		// Only on product pages
		if (!window.__PARTIFY_PRODUCT_COLLECTIONS) return;

		var result = resolve();

		// When no active vehicle, clear stale easysearch-preselect-fitment so
		// subsequent page loads don't show a false fitment result in EasySearch.
		if (result.state === "no-vehicle") clearStalePreselect();

		if (mode === "shadow") {
			// Compare with current EasySearch DOM state
			var esState =
				window.PartifyAdapter && window.PartifyAdapter.getFitmentStateFromDOM
					? window.PartifyAdapter.getFitmentStateFromDOM()
					: { state: "unknown" };

			// Classify parity:
			// MATCH          — both agree
			// STALE_PRESELECT — no-vehicle but EasySearch shows stale prior-session result
			// MISMATCH        — genuine resolver disagreement (e.g. native=fail, ES=success)
			var parity;
			if (result.state === esState.state) {
				parity = "MATCH";
			} else if (
				result.state === "no-vehicle" &&
				esState.state !== "no-vehicle"
			) {
				parity = "STALE_PRESELECT";
			} else {
				parity = "MISMATCH";
			}

			console.log("[PartifyFitment:shadow]", {
				native: result.state,
				easysearch: esState.state,
				parity: parity,
				vehicle: result.vehicle ? result.vehicle.ymm : "none",
				product: window.__PARTIFY_PRODUCT_HANDLE || "unknown",
				matchedHandle: result.matchedHandle || null,
				candidates: result.candidatesTried || [],
				collections: result.collectionCount || 0,
			});
			if (parity === "MISMATCH") {
				console.warn(
					"[PartifyFitment:shadow] MISMATCH — native=" +
						result.state +
						" easysearch=" +
						esState.state,
				);
			}
		}

		if (mode === "primary" || mode === true) {
			// Hide EasySearch embed to prevent race/flicker
			var embed = document.querySelector(".easysearch-embed-widget-fitment");
			if (embed) embed.style.display = "none";

			var adapter = window.PartifyAdapter;
			if (!adapter || !adapter.emitFitmentState) {
				console.warn("[PartifyFitment] PartifyAdapter not available");
				return;
			}
			var state = result.state === "error" ? "no-vehicle" : result.state;
			var href = state === "fail" ? buildFailHref(result.vehicle) : undefined;
			adapter.emitFitmentState(state, result.vehicle, href);

			// Direct native PDP gating — bypasses shim DOM roundtrip
			var detail = { state: state, vehicle: result.vehicle };
			window.__partifyNativeFitmentLastResult = detail;
			document.dispatchEvent(
				new CustomEvent("partify:native-fitment-resolved", {
					detail: detail,
				}),
			);
		}
	}

	/* ───────── auto-init ───────── */

	function init() {
		var mode = window.__PARTIFY_NATIVE_FITMENT;
		if (!mode) return;

		if (mode === "shadow") {
			// In shadow mode, wait for EasySearch to finish rendering first
			setTimeout(run, 2500);
		} else {
			// In primary mode, defer to next microtask so all DOMContentLoaded
			// handlers (e.g. product-page-customization) register their
			// partify:native-fitment-resolved listener first.
			setTimeout(run, 0);
		}

		// Re-resolve on vehicle change
		window.addEventListener("partify:vehicle:changed", function () {
			if (mode === "shadow") {
				setTimeout(run, 2500);
			} else {
				setTimeout(run, 50);
			}
		});
		window.addEventListener("searchTermsUpdated", function () {
			if (mode === "shadow") {
				setTimeout(run, 2500);
			} else {
				setTimeout(run, 50);
			}
		});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}

	/* ───────── public API for manual testing ───────── */

	window.PartifyFitment = {
		resolve: resolve,
		resolveVehicle: resolveVehicle,
		run: run,
		toHandle: toHandle,
		buildHandleCandidates: buildHandleCandidates,
		getActiveVehicle: getActiveVehicle,
	};
})();
